import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { BUILT_INDEX_STEPS } from "@/content/built-index";
import { cn } from "@/lib/utils";

interface AssessmentShellProps {
  /** 1-based index into BUILT_INDEX_STEPS. */
  step: number;
  title: string;
  intro: string;
  children: ReactNode;
  footer: ReactNode;
}

/**
 * Shared chrome for every Built Index assessment step: progress rail, heading,
 * question body and the sticky continue footer.
 */
export const AssessmentShell = ({
  step,
  title,
  intro,
  children,
  footer,
}: AssessmentShellProps) => {
  const total = BUILT_INDEX_STEPS.length;
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteNav />
      <section className="relative pt-32 pb-20 md:pt-36 bg-hero">
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,hsl(var(--gold)/0.09),transparent_55%)]"
        />
        <div className="container relative max-w-3xl">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <Link
              to="/built-index"
              className="text-[10px] uppercase tracking-[0.3em] text-gold"
            >
              The Built Index™
            </Link>
            <span className="text-[10px] uppercase tracking-[0.3em] text-silver">
              Step {step} of {total}
            </span>
          </div>

          <ol className="mt-4 grid grid-cols-4 gap-2" aria-label="Assessment progress">
            {BUILT_INDEX_STEPS.map((s, i) => (
              <li key={s.path} className="space-y-2">
                <span
                  className={cn(
                    "block h-1 w-full rounded-full",
                    i < step ? "bg-orange" : "bg-white/15",
                  )}
                />
                <span
                  className={cn(
                    "hidden sm:block text-[10px] uppercase tracking-[0.18em]",
                    i < step ? "text-white" : "text-silver/60",
                  )}
                >
                  {s.label}
                </span>
              </li>
            ))}
          </ol>

          <h1 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-8 leading-[1.05]">
            {title}
          </h1>
          <p className="text-silver mt-4 leading-relaxed">{intro}</p>

          <div className="mt-10 space-y-6">{children}</div>

          <div className="mt-10 border-t border-white/10 pt-6">{footer}</div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

interface ScaleQuestionProps {
  id: string;
  label: string;
  prompt: string;
  value?: number;
  onChange: (value: number) => void;
}

/** 1–10 self-rating grid used by the Built Diagnostic and the Listening Scale. */
export const ScaleQuestion = ({
  id,
  label,
  prompt,
  value,
  onChange,
}: ScaleQuestionProps) => (
  <fieldset className="border border-white/10 bg-white/[0.03] p-5">
    <legend className="px-2 text-[10px] uppercase tracking-[0.25em] text-gold">
      {label}
    </legend>
    <p className="text-white text-sm md:text-base leading-relaxed">{prompt}</p>
    <div className="mt-4 grid grid-cols-10 gap-1.5" role="radiogroup" aria-label={label}>
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${label}: ${n} out of 10`}
          onClick={() => onChange(n)}
          className={cn(
            "h-10 text-xs font-semibold transition-colors border",
            value === n
              ? "bg-orange text-primary-foreground border-orange"
              : "border-white/15 text-silver hover:border-gold/60 hover:text-white",
          )}
          id={`${id}-${n}`}
        >
          {n}
        </button>
      ))}
    </div>
    <div className="mt-2 flex justify-between text-[10px] uppercase tracking-[0.2em] text-silver/60">
      <span>Not true yet</span>
      <span>Fully true</span>
    </div>
  </fieldset>
);

interface ChoiceQuestionProps {
  id: string;
  index: number;
  prompt: string;
  options: Record<string, string>;
  value?: string;
  onChange: (value: string) => void;
}

/** Forced-choice card list used by DISC and Fuel & Drain. */
export const ChoiceQuestion = ({
  id,
  index,
  prompt,
  options,
  value,
  onChange,
}: ChoiceQuestionProps) => (
  <fieldset className="border border-white/10 bg-white/[0.03] p-5">
    <legend className="px-2 text-[10px] uppercase tracking-[0.25em] text-gold">
      Question {index}
    </legend>
    <p className="text-white text-sm md:text-base leading-relaxed">{prompt}</p>
    <div className="mt-4 grid gap-2" role="radiogroup" aria-label={prompt}>
      {Object.entries(options).map(([key, text]) => (
        <button
          key={key}
          type="button"
          role="radio"
          aria-checked={value === key}
          onClick={() => onChange(key)}
          id={`${id}-${key}`}
          className={cn(
            "text-left text-sm px-4 py-3 border transition-colors",
            value === key
              ? "border-orange bg-orange/15 text-white"
              : "border-white/12 text-silver hover:border-gold/60 hover:text-white",
          )}
        >
          {text}
        </button>
      ))}
    </div>
  </fieldset>
);
